/**
 * TrustGate Console - Bound Intent Detail View
 * 
 * Lifecycle Record View - shows entire governance lifecycle of a Bound Intent
 * as a controlled system record with timeline, receipts, and proof artifacts.
 * 
 * NOT a popup with fields - this is an audit record viewer.
 */

import React, { useState } from 'react';
import { LifecycleTimeline } from './LifecycleTimeline';
import { IntentRecordDetails } from './IntentRecordDetails';
import { ReceiptViewer } from './ReceiptViewer';
import { PolicyDiffModal } from './PolicyDiffModal';
import { getStatusMeta } from '../../shared/status';
import type { TrustGateEvent } from '../../ecs/types';

interface BoundIntentDetailViewProps {
  event: TrustGateEvent;
  onClose: () => void;
  isAdmin?: boolean;
}

export const BoundIntentDetailView: React.FC<BoundIntentDetailViewProps> = ({
  event,
  onClose,
  isAdmin = false,
}) => {
  const [selectedReceipt, setSelectedReceipt] = useState<'binding' | 'execution' | null>(null);
  const [showPolicyDiff, setShowPolicyDiff] = useState(false);

  const statusMeta = getStatusMeta(event.status);

  return (
    <>
      {/* Full-page overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-[#0a0a0f] border border-white/10 rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] mx-4 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#0a0a0f] to-[#1a1a2e]">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{statusMeta.icon}</span>
              <div>
                <h2 className="text-xl font-bold text-[#f8fafc]">
                  🔒 Bound Intent #{event.entityId.slice(0, 8).toUpperCase()}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-[#cbd5e1]">Status:</span>
                  <span className="text-sm font-semibold" style={{ color: statusMeta.color }}>
                    {statusMeta.icon} {statusMeta.label}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-[#cbd5e1] hover:text-[#f8fafc] transition-colors text-2xl font-light"
            >
              ✕
            </button>
          </div>

          {/* Two-column layout */}
          <div className="flex-1 flex overflow-hidden">
            {/* LEFT: Lifecycle Timeline */}
            <div className="w-2/5 border-r border-white/10 overflow-y-auto bg-[#0a0a0f]/50">
              <LifecycleTimeline 
                event={event}
                onViewReceipt={(type) => setSelectedReceipt(type)}
              />
            </div>

            {/* RIGHT: Intent Record Details */}
            <div className="flex-1 overflow-y-auto bg-[#0f0f14]/50">
              <IntentRecordDetails 
                event={event}
                onViewReceipt={(type: 'binding' | 'execution') => setSelectedReceipt(type)}
                onViewPolicyDiff={() => setShowPolicyDiff(true)}
                isAdmin={isAdmin}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Viewer Modal */}
      {selectedReceipt && (
        <ReceiptViewer
          event={event}
          receiptType={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
        />
      )}

      {/* Policy Diff Modal */}
      {showPolicyDiff && (
        <PolicyDiffModal
          bindingVersion={(event as any).policyVersion || 'v3.2.1'}
          currentVersion="v3.4.0"
          onClose={() => setShowPolicyDiff(false)}
        />
      )}
    </>
  );
};
